import React, { Component } from 'react';
import { connect } from 'react-redux';

import { Column } from '@appcues/component-library';
import Loader from 'components/Common/Loader';

import BillingDetails from 'components/Subscription/BillingDetails';
import BillingHistory from 'components/Subscription/BillingHistory';
import CanceledNotice from 'components/Subscription/CanceledNotice';
import CurrentPeriod from 'components/Subscription/CurrentPeriod';
import FreeTrial from 'components/Subscription/FreeTrial';
import InvoiceMessage from 'components/Subscription/InvoiceMessage';
import YourPlan from 'components/Subscription/YourPlan';
import ManageSubscription from 'components/Subscription/ManageSubscription';

import { getSettingsFromPlanId, updateCard } from 'utils/stripe';
import {
  BILLING_INTERVALS,
  DEFAULT_PLAN_LIMIT,
  PLAN_TO_TIER_MAPPINGS,
} from 'constants/plans';
import { isNpsOnlyPlan } from 'helpers/plans';
import {
  selectAccountMeta,
  selectAccountMetaSynced,
} from 'reducers/account/meta';
import { selectUser, selectUserId, selectUserSynced } from 'reducers/user';
import {
  selectAccountAllTimeStats,
  selectAccountAllTimeStatsSynced,
} from 'reducers/account/allTimeStats';
import { navigate } from 'actions/routing';

const getPlanLimit = (accountMeta, config, currentPlanId) => {
  // In rare cases a customer who is on an enterprise/unlimited plan may have an mau value of 1.
  // We don't want to actually show that for their MAU limit so skip any of those instances.
  if (Number(accountMeta.mau) > 1) {
    return accountMeta.mau;
  }
  if (isNpsOnlyPlan(currentPlanId)) {
    return DEFAULT_PLAN_LIMIT;
  }
  return config.limit;
};

const getPlanType = (accountMeta, currentPlanId) => {
  if (accountMeta.tier) {
    // Some values for tier inside accountMeta may have an underscore with a bunch of junk after it.
    // We want to exclude the underscore and all the subsequent junk. Hence the split function.
    return accountMeta.tier.split('_')[0];
  }
  return PLAN_TO_TIER_MAPPINGS[currentPlanId] || 'enterprise';
};

export class Subscription extends Component {
  navigateToStory = () => {
    const { navigate: navigateTo } = this.props;

    navigateTo('/subscription/story');
  };

  SubscriptionContent = ({ isStripeCustomer, currentStripeId }) => {
    const { accountMeta, billing, allTimeStats, user, userId } = this.props;

    const userWithAccountId = user && {
      email: user.email,
      id: userId.replace('simplelogin:', ''),
      accountId: accountMeta.id,
    };

    const currentPlanId = accountMeta.stripePlanId;
    const { isTrial } = accountMeta;
    const anonymousMaus = allTimeStats.monthlyAnonymousUsers;
    const subscription =
      billing.details && billing.details.subscriptions.data[0];
    const accountCanceledAt = accountMeta.stripeSubEndedAt;

    if (isTrial) {
      return <FreeTrial />;
    }

    let planLimit;
    let planType;

    if (currentPlanId) {
      const config = getSettingsFromPlanId(currentPlanId);
      if (config.type !== BILLING_INTERVALS.custom.name) {
        planLimit = getPlanLimit(accountMeta, config, currentPlanId);
        planType = getPlanType(accountMeta, currentPlanId);
      }
    }

    const { canceledAt } = accountMeta;

    return (
      <Column>
        {canceledAt && (
          <CanceledNotice
            canceledAt={canceledAt}
            billingDetails={billing.details}
          />
        )}
        {planType && <YourPlan planType={planType} planLimit={planLimit} />}
        <CurrentPeriod
          accountId={accountMeta.id}
          anonymousMaus={anonymousMaus}
          monthlyActiveUsers={allTimeStats.monthlyWebsiteUsers}
          planLimit={planLimit}
          subscription={subscription}
          uuid={accountMeta.uuid}
        />
        {isStripeCustomer ? (
          <>
            <BillingDetails
              billingDetails={billing.details}
              currentStripeId={currentStripeId}
              updateCard={updateCard}
              userWithAccountId={userWithAccountId}
            />
            <BillingHistory billingHistory={billing.history} />
          </>
        ) : (
          <InvoiceMessage />
        )}
        {!canceledAt && (
          <ManageSubscription
            accountCanceledAt={accountCanceledAt}
            billingDetails={billing.details}
            navigateToStory={this.navigateToStory}
          />
        )}
      </Column>
    );
  };

  render() {
    const { accountMeta, billing, synced } = this.props;

    const currentStripeId = accountMeta.stripeId;
    const isStripeCustomer =
      currentStripeId && currentStripeId.startsWith('cus_');
    const stripeSynced = billing.details && billing.history;

    if (!synced || (isStripeCustomer && !stripeSynced)) {
      return <Loader />;
    }

    return (
      <this.SubscriptionContent
        isStripeCustomer={isStripeCustomer}
        currentStripeId={currentStripeId}
      />
    );
  }
}

const mapStateToProps = state => {
  const synced =
    selectUserSynced(state) &&
    selectAccountMetaSynced(state) &&
    selectAccountAllTimeStatsSynced(state);
  return {
    accountMeta: selectAccountMeta(state),
    billing: state.billing,
    user: selectUser(state),
    allTimeStats: selectAccountAllTimeStats(state),
    userId: selectUserId(state),
    synced,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    navigate: path => dispatch(navigate(path)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Subscription);
